import React, { useEffect } from 'react';
import { X, IndianRupee, Sparkles } from 'lucide-react';

export default function VariantSelectionModal({ item, onSelectVariant, onClose }) {
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Close on Escape
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      // Select variant by number key (1, 2, 3, etc.)
      const digit = parseInt(e.key);
      if (!isNaN(digit) && digit >= 1 && digit <= item.variants.length) {
        const selectedVariant = item.variants[digit - 1];
        onSelectVariant(selectedVariant);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [item.variants, onSelectVariant, onClose]);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg transform animate-slideUp">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-t-2xl p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={20} />
          </button>

          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <Sparkles size={24} />
            </div>
            <div>
              <h3 className="text-xl font-bold">{item.name}</h3>
              <p className="text-sm text-white/80">Select a variant</p>
            </div>
          </div>
        </div>

        {/* Variants List */}
        <div className="p-6 space-y-3 max-h-[60vh] overflow-y-auto">
          {item.variants.map((variant, index) => (
            <button
              key={variant.id}
              onClick={() => onSelectVariant(variant)}
              className="w-full group relative overflow-hidden bg-gradient-to-br from-white to-slate-50 hover:from-teal-50 hover:to-cyan-50 border-2 border-slate-200 hover:border-teal-400 rounded-xl p-5 transition-all duration-200 hover:scale-[1.02] hover:shadow-lg"
            >
              {/* Keyboard Shortcut Badge */}
              <div className="absolute top-3 left-3 w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-600 text-white rounded-lg flex items-center justify-center font-black text-sm shadow-md group-hover:scale-110 transition-transform">
                {index + 1}
              </div>

              {/* Content */}
              <div className="ml-10 flex items-center justify-between">
                {/* Variant Name */}
                <div className="flex-1 text-left">
                  <p className="font-bold text-lg text-slate-900 group-hover:text-teal-900 transition-colors">
                    {variant.variantName}
                  </p>
                  {variant.isAvailable === false && (
                    <span className="text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full mt-1 inline-block">
                      Not Available
                    </span>
                  )}
                </div>

                {/* Price */}
                <div className="flex items-center gap-1 text-slate-900 group-hover:text-teal-900 transition-colors">
                  <IndianRupee size={20} className="opacity-70" />
                  <span className="text-2xl font-black">{variant.variantPrice.toFixed(0)}</span>
                </div>
              </div>

              {/* Hover Effect Line */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 to-cyan-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-full"></div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 bg-slate-50 rounded-b-2xl">
          <div className="flex items-center justify-between text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-white border border-slate-300 rounded shadow-sm font-mono">1-{item.variants.length}</kbd>
              <span>Quick select variant</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-white border border-slate-300 rounded shadow-sm font-mono">ESC</kbd>
              <span>Close</span>
            </div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
